/**
 * useAiChat — React hook for AI chat via the /functions/v1/ai_chat_message endpoint.
 *
 * Routes through the Worker (which proxies to LAAP) so no Anthropic API key
 * is ever required or used in the frontend. Streams SSE response deltas and
 * appends them to the assistant message in real time.
 */

import { useCallback, useState } from 'react';
import { apiClient } from '../lib/apiClient';
import { consumeAnthropicStream } from '../lib/parseAnthropicSse';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

export interface UseAiChatOptions {
  chatId: string;
  systemPrompt?: string;
  model?: string;
}

export interface UseAiChatReturn {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages an AI chat session backed by /functions/v1/ai_chat_message.
 *
 * @param options.chatId       - Unique chat session identifier (UUID).
 * @param options.systemPrompt - Optional system prompt forwarded to the model.
 * @param options.model        - Optional model override (defaults to Worker default).
 */
export function useAiChat({ chatId, systemPrompt, model }: UseAiChatOptions): UseAiChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(
    async (content: string) => {
      if (isStreaming) return;
      setError(null);

      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
      };

      // Add user message and a placeholder for the assistant reply.
      const assistantId = crypto.randomUUID();
      const assistantPlaceholder: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
      };

      setMessages((prev) => [...prev, userMsg, assistantPlaceholder]);
      setIsStreaming(true);

      // Build the messages array to send — include the new user message.
      const thread = [...messages, userMsg].map(({ role, content: c }) => ({
        role,
        content: c,
      }));

      try {
        const { stream, error: invokeError } = await apiClient.functions.invokeStream(
          'ai_chat_message',
          {
            body: {
              chatId,
              messages: thread,
              ...(systemPrompt !== undefined ? { systemPrompt } : {}),
              ...(model !== undefined ? { model } : {}),
            },
          },
        );

        if (invokeError || !stream) {
          setError(invokeError?.message ?? 'Failed to reach AI service');
          // Remove the empty assistant placeholder.
          setMessages((prev) => prev.filter((m) => m.id !== assistantId));
          return;
        }

        await consumeAnthropicStream(stream, (delta) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: m.content + delta } : m,
            ),
          );
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      } finally {
        setIsStreaming(false);
      }
    },
    [chatId, isStreaming, messages, model, systemPrompt],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return { messages, isStreaming, error, sendMessage, clearMessages };
}
